const DISCORD_WIDGET_URL = 'https://discord.com/api/guilds';
const SHIELDS_DYNAMIC_URL = 'https://img.shields.io/badge/dynamic/json.json';
const CACHE_TTL = 5 * 60 * 1000;
const REQUEST_TIMEOUT = 6000;
const requests = new Map();

function getCacheKey(guildId) {
  return `discord-presence:v3:${guildId}`;
}

function formatPresenceCount(count) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en', { useGrouping: false }).format(count);
}

function readCachedPresence(guildId) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(getCacheKey(guildId)));
    const isFresh = cached && Date.now() - cached.savedAt < CACHE_TTL;

    if (isFresh && typeof cached.displayCount === 'string' && cached.displayCount.length > 0) {
      return cached.displayCount;
    }

  } catch {
    // A blocked or malformed sessionStorage entry should not break the header.
  }

  return null;
}

function cachePresence(guildId, displayCount) {
  try {
    sessionStorage.setItem(
      getCacheKey(guildId),
      JSON.stringify({ displayCount, savedAt: Date.now() })
    );
  } catch {
    // The live value can still be displayed when storage is unavailable.
  }
}

async function fetchJson(url) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      credentials: 'omit',
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`Presence service returned ${response.status}`);
    return response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

async function fetchDiscordWidgetPresence(guildId) {
  const payload = await fetchJson(`${DISCORD_WIDGET_URL}/${encodeURIComponent(guildId)}/widget.json`);
  const count = Number(payload.presence_count);

  if (!Number.isInteger(count) || count < 0) throw new Error('Discord widget returned an invalid count');
  return formatPresenceCount(count);
}

async function fetchShieldsPresence(guildId) {
  const widgetUrl = `${DISCORD_WIDGET_URL}/${encodeURIComponent(guildId)}/widget.json`;
  const query = new URLSearchParams({
    url: widgetUrl,
    query: '$.presence_count',
    label: '',
    color: 'brightgreen',
  });
  const payload = await fetchJson(`${SHIELDS_DYNAMIC_URL}?${query}`);
  const value = String(payload.value || payload.message || '').trim();
  const count = Number(value);

  if (!Number.isInteger(count) || count < 0) throw new Error('Shields returned an invalid count');
  return formatPresenceCount(count);
}

async function fetchPresence(guildId) {
  const cached = readCachedPresence(guildId);
  if (cached !== null) return cached;

  if (requests.has(guildId)) return requests.get(guildId);

  const request = (async () => {
    try {
      let displayCount;

      try {
        displayCount = await fetchDiscordWidgetPresence(guildId);
      } catch {
        displayCount = await fetchShieldsPresence(guildId);
      }

      cachePresence(guildId, displayCount);
      return displayCount;
    } finally {
      requests.delete(guildId);
    }
  })();

  requests.set(guildId, request);
  return request;
}

class DiscordPresence extends HTMLElement {
  connectedCallback() {
    if (this.refreshTimer) return;

    void this.refresh();
    this.refreshTimer = window.setInterval(() => void this.refresh(), CACHE_TTL);
  }

  disconnectedCallback() {
    window.clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }

  async refresh() {
    const guildId = this.dataset.guildId;
    const label = this.querySelector('[data-discord-presence-label]');
    if (!guildId || !label) return;

    try {
      const displayCount = await fetchPresence(guildId);

      label.textContent = `${displayCount} online`;
      this.dataset.state = 'online';
      this.hidden = false;
      this.closest('a')?.setAttribute(
        'aria-label',
        `Join the UEFN Dream Store Discord server. Approximately ${displayCount} members online.`
      );
    } catch {
      this.dataset.state = 'unavailable';
      this.hidden = true;
      this.closest('a')?.setAttribute('aria-label', 'Join the UEFN Dream Store Discord server');
    }
  }
}

if (!customElements.get('discord-presence')) {
  customElements.define('discord-presence', DiscordPresence);
}
